import { needsMultiMetricData } from '../../DataProvider/utils/handlebars-field-config';
import type { ChartType } from '../../PageConfigProvider/types/chart';
import {
  isDynamicDivergingBarComboPlot,
  isShareOfSegmentPlot,
  type PositionedItem,
} from '../../PageConfigProvider/types/item';

type SpecialChartType = Extract<
  ChartType,
  | 'entity-summary'
  | 'mixed-stats'
  | 'icicle'
  | 'subsidiary-snapshot'
  | 'subsidiary-trend'
  | 'timeline'
  | 'share-of-segment'
  | 'sentiment_review'
  | 'map'
  | 'text'
  | 'ai-summary'
  | 'image'
>;

// Lives here (not in SpecialChart.tsx) so route consumers — including
// DataWrappedChart and its specs — don't transitively load the whole
// SpecialChart component graph just to classify a chart type.
const SpecialChartTypes: Record<SpecialChartType, boolean> = {
  'entity-summary': true,
  'mixed-stats': true,
  icicle: true,
  'subsidiary-snapshot': true,
  'subsidiary-trend': true,
  timeline: true,
  'share-of-segment': true,
  sentiment_review: true,
  map: true,
  text: true,
  'ai-summary': true,
  image: true,
};

export const isSpecialChartType = (
  chartType: ChartType
): chartType is SpecialChartType =>
  SpecialChartTypes[chartType as SpecialChartType];

export type ChartDataRoute =
  | 'table'
  | 'special'
  | 'snowflake'
  | 'multi-metric'
  | 'main';

/**
 * THE chart data-route decision, shared by the real chart tree
 * (ChartRootSelector → DataWrappedChart) and the assistant's headless digest
 * router (HeadlessDigestProviderRouter). Both must branch off this one
 * function: when the headless mirror drifts from the real tree, the failure
 * mode is a digest that hangs to its 30s timeout and resolves EMPTY —
 * indistinguishable from a sick backend. Order matters and mirrors the real
 * tree: table and special are decided before the data-source checks because
 * ChartRootSelector routes them away before DataWrappedChart ever mounts.
 */
export const classifyChartDataRoute = (
  item: PositionedItem
): ChartDataRoute => {
  const chartType = item.plot?.chartType;
  if (!chartType) return 'main';
  if (chartType === 'table') return 'table';
  if (
    isSpecialChartType(chartType) ||
    isShareOfSegmentPlot(item.plot) ||
    isDynamicDivergingBarComboPlot(item.plot)
  )
    return 'special';
  if (item.plot?.dataSource === 'snowflake' && item.plot?.snowflakeConfig) {
    return 'snowflake';
  }
  if (needsMultiMetricData(item, chartType)) return 'multi-metric';
  return 'main';
};
